import {
  CountryCode,
  CustomGetProcessRequestQuery,
  CustomProcessBySlugQuery,
  GlobalVisaType,
  PersonType,
  ProcessRequest,
  ProcessRequestState,
  SearchableProcessRequestFilterInput,
  SearchProcessRequestsQuery,
  VisaAdditionalProcessing,
  VisaStatus,
  VisaSubCategory,
} from 'API';
import {
  processBySlug,
  getProcessRequest,
  searchProcessRequests,
  updateProcessRequest,
} from 'graphql/customProcessQueries';
import { nanoid } from 'nanoid';
import slugify from 'slugify';
import labels from 'shared/labels/processRequestLabels';
import { SLUGIFY_OPTIONS } from 'shared/constants';
import { resolvers } from 'shared/enums';
import { getPayload, lower, possessive } from 'shared/utilities';
import ActivityService from 'services/activity';
import PersonService from 'services/person';
import { ROUTES } from 'shared/routes';
import { Link } from 'react-router-dom';
import { ActivityType } from 'models';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';
import { post } from 'aws-amplify/api';

const processResolver = resolvers.VisaStatus;
const { fullname } = PersonService;

class ProcessService {
  crimeFields: Array<string> = [
    'tortureGenocide',
    'killing',
    'severeInjuring',
    'nonconsensualSex',
    'religiousBelief',
  ];

  archive = async (process: ProcessRequest) => {
    const personID = PersonService.getPersonID();
    const personType = PersonService.getPersonType();

    const response = client.graphql({
      query: updateProcessRequest,
      variables: {
        input: {
          id: process.id,
          state: ProcessRequestState.ARCHIVED,
          requestDate: process.requestDate,
          archivedDate: new Date().toISOString(),
        },
      },
    });

    let recipients = [];
    if (process.individualSponsored) {
      recipients = [process.assignedToID];
    } else {
      recipients = [process!.company?.accountManagerID];

      const isAssignedToEM = process?.assignedToEmployer?.id === process!.assignedToID;
      // Don't want to notify EM
      if (!isAssignedToEM) recipients = [...recipients, process!.assignedToID];
    }

    await ActivityService.createActivity({
      activityType: ActivityType.PROCESS_REQUEST_ARCHIVED,
      actorID: personID,
      actorType: personType,
      directTargetID: process.id!,
    },
    recipients);

    return response;
  };

  fullname = (process: ProcessRequest, personalize?: boolean, actorID?: string): string => {
    const personID = PersonService.getPersonID();
    const { foreignNational } = process;
    const { firstname, lastname } = process.employeeData || {
      firstname: '-',
      lastname: '-',
    };

    const requestName = process.requestedProcess && process.requestedProcess !== VisaStatus.NOT_SURE
      ? `${processResolver(process.requestedProcess)} process request`
      : 'process request';

    if (personalize && foreignNational && (foreignNational.id === personID)) {
      return `your ${requestName}`;
    }

    if (personalize && (actorID === foreignNational?.id)) {
      return `their ${requestName}`;
    }

    if (foreignNational) {
      return `${possessive(fullname(foreignNational))} ${requestName}`;
    }

    return `${possessive(`${firstname} ${lastname}`)} ${requestName}`;
  };

  getBySlug = async (slug: string): Promise<ProcessRequest | null> => {
    try {
      const slugResponse = (await client.graphql({
        query: processBySlug,
        variables: { slug },
      })) as GraphQLResult<CustomProcessBySlugQuery>;

      if (!slugResponse.data?.processBySlug?.items![0]) {
        return null;
      }

      const { id } = slugResponse.data.processBySlug.items[0];

      const response = (await client.graphql({
        query: getProcessRequest,
        variables: { id },
      })) as GraphQLResult<CustomGetProcessRequestQuery>;

      return response.data?.getProcessRequest
        ? (response.data.getProcessRequest as ProcessRequest)
        : null;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  /**
   * Nanoid suffix pattern: hyphen + 8 URL-safe chars.
   * Used to verify slug has unique suffix before sending to DB.
   */
  private static NANOID_SUFFIX_REGEX = /-[A-Za-z0-9_-]{8}$/;

  /**
   * Returns true if slug has the nanoid suffix (unique identifier).
   * Prevents sending old-format slugs that could cause duplicates.
   */
  hasSlugUniqueSuffix = (slug: string | null | undefined): boolean => Boolean(
    slug && ProcessService.NANOID_SUFFIX_REGEX.test(slug),
  );

  /**
   * Generates a unique slug for a process by slugifying the base string
   * and appending a short nanoid suffix. The suffix guarantees uniqueness
   * without requiring an API check, eliminating TOCTOU races.
   * @param {string} base - e.g. "John Doe H1B"
   * @returns {string} e.g. "john-doe-h1b-x7k2m9q1"
   */
  generateSlug = async (base: string): Promise<string> => {
    const slugBase = slugify(base, SLUGIFY_OPTIONS);
    const suffix = nanoid(8);

    return `${slugBase}-${suffix}`;
  };

  getLabel = (fieldName: string): string => labels[fieldName] || '';

  initials = (process: ProcessRequest): string => {
    if (process.foreignNational) {
      return `${process.foreignNational.firstname?.slice(0, 1) || ''}${
        process.foreignNational.lastname?.slice(0, 1) || ''
      }`;
    }

    if (process.employeeData) {
      return `${process.employeeData.firstname?.slice(0, 1) || ''}${
        process.employeeData.lastname?.slice(0, 1) || ''
      }`;
    }

    return '-';
  };

  link = (process: ProcessRequest) => `${ROUTES.processRequests}/${process.slug}`;

  linkName = (
    process: ProcessRequest, personalize?: boolean, actorID?: string,
  ) => (
    <Link
      to={this.link(process)}
    >
      {this.fullname(process, personalize, actorID)}
    </Link>
  );

  searchByName: SearchByName<ProcessRequest> = async (
    name?: string,
    maxCount?: number,
    state?: ProcessRequestState,
  ) => {
    if (!name) {
      return [];
    }

    const chunks = lower(name).split(' ');

    const andArray: Array<SearchableProcessRequestFilterInput> = [
      {
        state: {
          eq: state || ProcessRequestState.APPROVED,
        },
      },
    ];

    chunks.forEach((chunk) => {
      andArray.push({ lowerNationalName: { wildcard: `*${chunk}*` } });
    });

    try {
      const response = (await client.graphql({
        query: searchProcessRequests,
        variables: {
          and: andArray,
          limit: maxCount || 20,
        },
      })) as GraphQLResult<SearchProcessRequestsQuery>;

      return (response.data?.searchProcessRequests?.items
        || []) as Array<ProcessRequest>;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  unarchive = (process: ProcessRequest) => client.graphql({
    query: updateProcessRequest,
    variables: {
      input: {
        id: process.id,
        state: ProcessRequestState.REQUESTED,
        requestDate: process.requestDate,
        archivedDate: null,
      },
    },
  });

  /**
   *
   * @param id Process Request
   * @param assignedToID New assignee
   * @param needsEmployerRevision Whether assignee is Employer
   * @param person Person making assignment
   */
  updateAssignee = async (
    id: string,
    assignedToID: string,
    needsEmployerRevision: boolean,
    person: Person,
  ) => {
    try {
      await client.graphql({
        query: updateProcessRequest,
        variables: {
          input: {
            id,
            assignedToID,
            assignedToEmployerID: assignedToID,
            assignedToTeamMemberID: assignedToID,
            needsEmployerRevision,
          },
        },
      });

      if (person.type !== PersonType.Employer) {
        const body = {
          input: {
            activityType: ActivityType.PROCESS_REQUEST_REASSIGNED,
            actorID: person.id,
            directTargetID: id,
            indirectTargetID: assignedToID,
          },
          recipients: [assignedToID],
        };
        const payload = await getPayload(body);

        await post({ apiName: 'utilitiesapi', path: '/activities', options: payload }).response;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  updateSubCategory = async (id: string, subCategory: VisaSubCategory) => {
    try {
      await client.graphql({
        query: updateProcessRequest,
        variables: {
          input: {
            id,
            requestedProcessSubCategory: subCategory,
          },
        },
      });
    } catch (err) {
      throw new Error();
    }
  };

  updateProcessAdditionalProcessing = async (id: string, additionalProcessing: VisaAdditionalProcessing[]) => {
    try {
      await client.graphql({
        query: updateProcessRequest,
        variables: {
          input: {
            id,
            requestedProcessAdditionalProcessing: additionalProcessing,
          },
        },
      });
    } catch (err) {
      throw new Error();
    }
  };

  updateCustomSubCategory = async (
    id: string,
    subCategory: string,
    countryCode: CountryCode,
    visaType: GlobalVisaType | null,
  ) => {
    try {
      await client.graphql({
        query: updateProcessRequest,
        variables: {
          input: {
            id,
            globalRequestedProcessSubCategory: subCategory,
            globalProcessCountryCode: countryCode,
            globalVisaType: visaType,
          },
        },
      });
    } catch (err) {
      throw new Error();
    }
  };
}

export default new ProcessService();
